import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { TlaaqLogo } from '../brand/TlaaqLogo'
import { LocaleSwitcher } from '../locale/LocaleSwitcher'
import type { Locale } from '../../i18n'
import { Icon, I } from '../ui/icons'
import { Pill } from '../ui/Pill'
import { Avatar } from '../ui/Avatar'
import { useIsMobileViewport } from '../../hooks/useIsMobileViewport'

export type NavPage =
  | 'home' | 'analytics' | 'orders' | 'products'
  | 'channels' | 'inventory' | 'warehouses'
  | 'team'

interface NavItem {
  id: NavPage
  labelKey: string
  ico: ReactNode
  count?: number
}

const OPERATE: NavItem[] = [
  { id: 'home',      labelKey: 'nav.items.home',      ico: I.home },
  { id: 'analytics', labelKey: 'nav.items.analytics', ico: I.trend },
  { id: 'orders',   labelKey: 'nav.items.orders',   ico: I.cart },
  { id: 'products', labelKey: 'nav.items.products', ico: I.package },
]

const CONNECT: NavItem[] = [
  { id: 'channels',   labelKey: 'nav.items.channels',   ico: I.globe },
  { id: 'inventory',  labelKey: 'nav.items.inventory',  ico: I.layers },
  { id: 'warehouses', labelKey: 'nav.items.warehouses', ico: I.warehouse },
]

const SETUP: NavItem[] = [
  { id: 'team', labelKey: 'nav.items.team', ico: I.users },
]

const NAV_ITEMS = [...OPERATE, ...CONNECT, ...SETUP]

export interface AppShellViewer {
  fullName: string
  email: string
  organizationName: string
}

interface AppShellProps {
  active: NavPage
  onNavigate: (page: NavPage) => void
  title: string
  subtitle?: string
  badge?: string
  navCounts?: Partial<Record<NavPage, number>>
  viewer?: AppShellViewer
  onLogout?: () => void
  onLocaleChange?: (locale: Locale) => void | Promise<void>
  onOpenImports?: () => void
  importsRunningCount?: number
  importsAttentionCount?: number
  children: ReactNode
}

export function AppShell({ active, onNavigate, title, subtitle, badge, navCounts, viewer, onLogout, onLocaleChange, onOpenImports, importsRunningCount = 0, importsAttentionCount = 0, children }: AppShellProps) {
  const isMobile = useIsMobileViewport()

  return (
    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', background: 'var(--bg)', height: '100%', minWidth: 0, overflow: 'hidden' }}>
      {!isMobile && <Sidebar active={active} onNavigate={onNavigate} navCounts={navCounts} viewer={viewer} />}
      <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <TopBar title={title} subtitle={subtitle} badge={badge} viewer={viewer} onLogout={onLogout} onLocaleChange={onLocaleChange} isMobile={isMobile} onOpenImports={onOpenImports} importsRunningCount={importsRunningCount} importsAttentionCount={importsAttentionCount} />
        {isMobile && <MobileNav active={active} onNavigate={onNavigate} navCounts={navCounts} />}
        <div style={{ flex: 1, padding: isMobile ? '18px 14px 96px' : '28px 32px', overflowY: 'auto', overflowX: 'hidden', minWidth: 0 }}>
          {children}
        </div>
      </main>
    </div>
  )
}

interface SidebarProps {
  active: NavPage
  onNavigate: (page: NavPage) => void
  navCounts?: Partial<Record<NavPage, number>>
  viewer?: AppShellViewer
}

function Sidebar({ active, onNavigate, navCounts, viewer }: SidebarProps) {
  const { t } = useTranslation()
  return (
    <aside style={{
      width: 232,
      background: 'white',
      borderInlineEnd: '1px solid var(--border-subtle)',
      padding: '20px 14px',
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      flexShrink: 0,
    }}>
      <div style={{ padding: '6px 8px 22px' }}>
        <TlaaqLogo height={24} />
      </div>

      <SidebarGroup label={t('nav.groups.operate')} />
      {OPERATE.map(item => (
        <SidebarItem key={item.id} item={{ ...item, count: navCounts?.[item.id] }} label={t(item.labelKey)} active={active} onClick={() => onNavigate(item.id)} />
      ))}

      <SidebarGroup label={t('nav.groups.connect')} />
      {CONNECT.map(item => (
        <SidebarItem key={item.id} item={item} label={t(item.labelKey)} active={active} onClick={() => onNavigate(item.id)} />
      ))}

      <SidebarGroup label={t('nav.groups.setup')} />
      {SETUP.map(item => (
        <SidebarItem key={item.id} item={item} label={t(item.labelKey)} active={active} onClick={() => onNavigate(item.id)} />
      ))}

      <div style={{ flex: 1 }} />

      <OrgChip viewer={viewer} />
    </aside>
  )
}

function SidebarGroup({ label }: { label: string }) {
  return (
    <div style={{
      fontSize: 10.5,
      fontWeight: 800,
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      color: 'var(--fg-subtle)',
      padding: '14px 10px 6px',
    }}>
      {label}
    </div>
  )
}

interface SidebarItemProps {
  item: NavItem
  label: string
  active: NavPage
  onClick: () => void
}

function SidebarItem({ item, label, active, onClick }: SidebarItemProps) {
  const isActive = item.id === active
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 11,
        padding: '9px 11px',
        borderRadius: 9,
        border: 'none',
        cursor: 'pointer',
        textAlign: 'start',
        background: isActive ? 'var(--brand-100)' : 'transparent',
        color: isActive ? 'var(--brand-700)' : 'var(--fg-muted)',
        fontWeight: isActive ? 700 : 500,
        fontSize: 13.5,
        fontFamily: 'inherit',
        width: '100%',
        position: 'relative',
        transition: 'all 150ms var(--ease-out)',
      }}
    >
      {isActive && (
        <div style={{
          position: 'absolute',
          insetInlineStart: 0,
          top: 8,
          bottom: 8,
          width: 3,
          borderRadius: 2,
          background: 'var(--brand-500)',
        }} />
      )}
      <Icon size={17}>{item.ico}</Icon>
      <span style={{ flex: 1 }}>{label}</span>
      {item.count != null && <Pill tone="brand" size="sm">{item.count}</Pill>}
    </button>
  )
}

function MobileNav({ active, onNavigate, navCounts }: Omit<SidebarProps, 'viewer'>) {
  const { t } = useTranslation()

  return (
    <nav
      aria-label={t('nav.mobile.label')}
      style={{
        display: 'flex',
        gap: 8,
        overflowX: 'auto',
        padding: '8px 14px',
        background: 'white',
        borderBottom: '1px solid var(--border-subtle)',
        flexShrink: 0,
      }}
    >
      {NAV_ITEMS.map(item => {
        const isActive = item.id === active
        const count = item.id === 'orders' ? navCounts?.orders : undefined

        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onNavigate(item.id)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              paddingBlock: 8,
              paddingInline: 11,
              borderRadius: 999,
              border: isActive ? '1px solid var(--brand-300)' : '1px solid var(--border-subtle)',
              background: isActive ? 'var(--brand-100)' : 'var(--bg-card)',
              color: isActive ? 'var(--brand-700)' : 'var(--fg-muted)',
              fontSize: 12.5,
              fontWeight: isActive ? 800 : 600,
              fontFamily: 'inherit',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            <Icon size={15}>{item.ico}</Icon>
            <span>{t(item.labelKey)}</span>
            {count != null && <Pill tone="brand" size="sm">{count}</Pill>}
          </button>
        )
      })}
    </nav>
  )
}

function OrgChip({ viewer }: { viewer?: AppShellViewer }) {
  const { t } = useTranslation()
  const organizationName = viewer?.organizationName ?? t('nav.demo.organization')
  const emailDomain = viewer?.email.split('@')[1] ?? t('nav.demo.domain')
  const mark = organizationName.trim().charAt(0).toUpperCase() || 'T'

  return (
    <div style={{
      padding: 12,
      borderRadius: 12,
      background: 'var(--ink-50)',
      border: '1px solid var(--border-subtle)',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
    }}>
      <div style={{
        width: 30,
        height: 30,
        borderRadius: 8,
        background: 'var(--brand-900)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        fontWeight: 800,
        fontFamily: 'Nunito, sans-serif',
        flexShrink: 0,
      }}>
        {mark}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--fg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {organizationName}
        </div>
        <div style={{ fontSize: 10.5, color: 'var(--fg-subtle)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{emailDomain}</div>
      </div>
    </div>
  )
}

interface TopBarProps {
  title: string
  subtitle?: string
  badge?: string
  viewer?: AppShellViewer
  onLogout?: () => void
  onLocaleChange?: (locale: Locale) => void | Promise<void>
  isMobile: boolean
  onOpenImports?: () => void
  importsRunningCount: number
  importsAttentionCount: number
}

function TopBar({ title, subtitle, badge, viewer, onLogout, onLocaleChange, isMobile, onOpenImports, importsRunningCount, importsAttentionCount }: TopBarProps) {
  const { t } = useTranslation()
  return (
    <div style={{
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'stretch' : 'center',
      justifyContent: 'space-between',
      padding: isMobile ? '12px 14px' : '14px 32px',
      borderBottom: '1px solid var(--border-subtle)',
      background: 'rgba(255,255,255,0.85)',
      backdropFilter: 'blur(10px)',
      gap: isMobile ? 12 : 24,
      flexShrink: 0,
    }}>
      {isMobile ? (
        <>
          <TopBarTitle title={title} subtitle={subtitle} badge={badge} isMobile={isMobile} />
          <TopBarActions t={t} viewer={viewer} onLogout={onLogout} onLocaleChange={onLocaleChange} isMobile={isMobile} onOpenImports={onOpenImports} importsRunningCount={importsRunningCount} importsAttentionCount={importsAttentionCount} />
        </>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 200 }}>
            <TopBarTitle title={title} subtitle={subtitle} badge={badge} isMobile={isMobile} />
          </div>
          <TopBarActions t={t} viewer={viewer} onLogout={onLogout} onLocaleChange={onLocaleChange} isMobile={isMobile} onOpenImports={onOpenImports} importsRunningCount={importsRunningCount} importsAttentionCount={importsAttentionCount} />
        </>
      )}
    </div>
  )
}

function TopBarTitle({ title, subtitle, badge, isMobile }: {
  title: string
  subtitle?: string
  badge?: string
  isMobile: boolean
}) {
  return (
    <div style={{ minWidth: 0, width: isMobile ? '100%' : undefined }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: isMobile ? 18 : 20, fontWeight: 800, color: 'var(--brand-900)', letterSpacing: 0, lineHeight: 1.2, margin: 0, overflowWrap: 'normal', wordBreak: 'normal' }}>
          {title}
        </h1>
        {badge && <Pill tone="brand" dot>{badge}</Pill>}
      </div>
      {subtitle && (
        <div style={{ fontSize: 12, color: 'var(--fg-subtle)', marginTop: 2 }}>{subtitle}</div>
      )}
    </div>
  )
}

function TopBarActions({ t, viewer, onLogout, onLocaleChange, isMobile, onOpenImports, importsRunningCount, importsAttentionCount }: {
  t: ReturnType<typeof useTranslation>['t']
  viewer?: AppShellViewer
  onLogout?: () => void
  onLocaleChange?: (locale: Locale) => void | Promise<void>
  isMobile: boolean
  onOpenImports?: () => void
  importsRunningCount: number
  importsAttentionCount: number
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 10, flexWrap: isMobile ? 'wrap' : 'nowrap', justifyContent: 'flex-start', flexShrink: 0, width: isMobile ? '100%' : undefined, maxWidth: isMobile ? '100%' : undefined, paddingBottom: isMobile ? 2 : undefined }}>
      {onOpenImports && <button aria-label={t('imports.title')} type="button" onClick={onOpenImports} style={{ background: importsAttentionCount ? 'var(--warning-100)' : 'transparent', border: '1px solid var(--border-subtle)', height: 36, paddingInline: 10, borderRadius: 10, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: 'var(--fg-muted)', fontFamily: 'inherit', fontWeight: 700, flexShrink: 0 }}><Icon size={15}>{I.download}</Icon>{!isMobile && t('imports.title')}{importsRunningCount > 0 && <Pill tone="brand" size="sm">{importsRunningCount}</Pill>}{importsAttentionCount > 0 && <Pill tone="warning" size="sm">{importsAttentionCount}</Pill>}</button>}
      {onLogout && (
        <button
          type="button"
          onClick={onLogout}
          style={{
            background: 'transparent',
            border: '1px solid var(--border-subtle)',
            height: 36,
            paddingInline: 12,
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            color: 'var(--fg-muted)',
            fontSize: 12.5,
            fontFamily: 'inherit',
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {t('nav.topbar.signOut')}
        </button>
      )}
      <div style={{ flexShrink: 0 }}>
        <LocaleSwitcher onLocaleChange={onLocaleChange} />
      </div>
      <div style={{ flexShrink: 0 }}>
        <Avatar name={viewer?.fullName ?? t('nav.demo.avatarName')} size={34} />
      </div>
    </div>
  )
}
